import {
	type MonoTypeOperatorFunction,
	type Observable,
	flatMap,
	fromEvent,
	iif,
	of,
	pipe,
	range,
	retryWhen,
	switchMap,
	take,
	tap,
	throwError,
	timer,
	zip,
} from "@suse-o11y/rxjs";
import { type WebSocketSubject, webSocket } from "@suse-o11y/rxjs/web-socket";
import noop from "lodash/noop";

import { type Dto, isInternalError, isInvalidStreamError, isStreamFailed } from "~/models/dto";

import { notification } from "../components/react/notification/Notification";
import { isOnline } from "../generic/Util";

export const createWebSocketSubject = <T>(url: string): WebSocketSubject<T> => webSocket({ url });

export const retryStream = <T>(
	retryDelay: number,
	onRetry: (online: boolean) => void = noop,
): MonoTypeOperatorFunction<T> =>
	retryWhen((errors: Observable<any>) =>
		errors.pipe(
			switchMap(() =>
				of(isOnline()).pipe(
					// Call onRetry first with the value of isOnline() at the moment of the error
					tap(onRetry),
					// Then either
					flatMap((online) =>
						iif(
							() => online,
							// Try again in a second if internet is okay but the server is down
							timer(retryDelay),
							// Or wait for the interwebz to come back
							// biome-ignore lint/style/noRestrictedGlobals: legacy
							fromEvent(window, "online").pipe(take(1)),
						),
					),
				),
			),
		),
	);

export const failStreamOnError = <T extends Dto>(): MonoTypeOperatorFunction<T> =>
	flatMap((value: T) => {
		if (isInternalError(value) || isInvalidStreamError(value) || isStreamFailed(value)) {
			notification.warning({ message: `Error on WebSocket stream: ${value.message}` });
			return throwError(value);
		}

		return of(value);
	});

/**
 * Retry an observable at most maxRetries times with exponential delay between retries.
 * @param maxRetries
 * @param delay
 */
export const retryWithExponentialBackoff = <T>(maxRetries: number, delay: number): MonoTypeOperatorFunction<T> =>
	pipe(
		retryWhen((errors) =>
			zip(range(0, maxRetries + 1), errors).pipe(
				flatMap(([iteration, error]) => {
					const i = iteration + 1;
					return iteration < maxRetries ? timer(i * i * delay) : throwError(error);
				}),
			),
		),
	);
